import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { motion } from "framer-motion";

// Données des slides (seules les images sont définies ici, les textes viennent des traductions)
const slides = [
  {
    id: 1,
    image: "images/hero1.png"
  },
  {
    id: 2,
    image: "images/hero2.jpg"
  },
  {
    id: 3,
    image: "images/hero3.png"
  }
];

export default function Hero() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const heroRef = useRef(null);

  // Gestion du swipe sur mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    // Vérifier si l'élément touché est un bouton ou un lien
    const target = e.target;
    const isInteractiveElement = target.closest('button') || target.closest('a') || target.closest('[role="button"]');
    
    // Si c'est un élément interactif, ne pas traiter comme un swipe
    if (isInteractiveElement) {
      return;
    }
    
    const difference = touchStartX.current - touchEndX.current;
    // Si le swipe est assez long (plus de 50px)
    if (Math.abs(difference) > 50) {
      if (difference > 0) {
        // Swipe vers la gauche - slide suivant
        nextSlide();
      } else {
        // Swipe vers la droite - slide précédent
        prevSlide();
      }
    }
  };

  // Auto-avancement des slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // Change de slide toutes les 6 secondes

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Fonction utilitaire pour obtenir le texte traduit d'un slide
  // eslint-disable-next-line no-unused-vars
  const getSlideText = (slideId, field) => {
    return t(`home.hero.slides.slide${slideId}.${field}`);
  };

  return (
    <section 
      className="relative bg-white overflow-hidden h-[500px] sm:h-[600px] md:h-[700px] lg:h-[800px]" 
      id="hero"
      ref={heroRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Carrousel avec effet de parallaxe */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Image de fond avec effet de parallaxe */}
            <motion.div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-[1.05]"
              style={{ backgroundImage: `url(${slide.image})` }}
              animate={{
                scale: index === currentSlide ? 1.05 : 1,
                x: index === currentSlide ? '-2%' : '0%'
              }}
              transition={{ duration: 8, ease: "easeInOut" }}
            >
              <img 
                src={slide.image} 
                alt="" 
                className="w-full h-full object-cover hidden" 
                aria-hidden="true"
              />
            </motion.div>
            
            {/* Overlay - dégradé moderne avec motif */}
            <div className="absolute inset-0 bg-gradient-to-br from-socabeg-blue/90 via-socabeg-blue/70 to-socabeg-red/50 z-10">
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2)_1px,transparent_1px)] bg-[length:24px_24px]"></div>
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-socabeg-blue/80 to-transparent"></div>
            </div>
            
            {/* Contenu du slide avec animation */}
            <div className="container mx-auto px-4 h-full flex flex-col justify-center pt-16 md:pt-0 relative z-10 text-white">
              <motion.div
                className="max-w-6xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{
                  opacity: index === currentSlide ? 1 : 0,
                  y: index === currentSlide ? 0 : 30
                }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Élément décoratif */}
                <motion.div 
                  className="h-1 bg-socabeg-red mb-6 block"
                  initial={{ width: 0 }}
                  animate={{ width: index === currentSlide ? 80 : 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                ></motion.div>
                
                <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight tracking-tight">
                  <div className="max-w-5xl">
                    {getSlideText(slide.id, 'title').split('\n').map((line, i) => (
                      <motion.span 
                        key={i} 
                        className="block"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                          opacity: index === currentSlide ? 1 : 0,
                          y: index === currentSlide ? 0 : 20
                        }}
                        transition={{ duration: 0.5, delay: 0.3 + (i * 0.1) }}
                      >
                        {line}
                      </motion.span>
                    ))}
                  </div>
                </h1>
                
                {getSlideText(slide.id, 'subtitle') && (
                  <motion.p 
                    className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-10 max-w-2xl text-white/90 font-light"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: index === currentSlide ? 1 : 0,
                      y: index === currentSlide ? 0 : 20
                    }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {getSlideText(slide.id, 'subtitle')}
                  </motion.p>
                )}
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: index === currentSlide ? 1 : 0,
                    y: index === currentSlide ? 0 : 20
                  }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="flex flex-wrap gap-4 mt-6 md:mt-8"
                >
                  <Link
                    to="/contact"
                    className="bg-socabeg-red hover:bg-socabeg-red/90 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-medium transition-all duration-300 inline-flex items-center group shadow-lg shadow-socabeg-red/20 text-sm sm:text-base"
                  >
                    {t('home.hero.contact')}
                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        ))}

        {/* Contrôles de navigation modernisés */}
        <motion.button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-socabeg-blue/20 backdrop-blur-sm text-white p-3 rounded-full z-20 hover:bg-socabeg-blue/30 transition-all duration-300 border border-socabeg-blue/30 shadow-lg hidden md:block"
          aria-label={t('home.common.previous')}
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaChevronLeft size={16} />
        </motion.button>
        
        <motion.button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-socabeg-blue/20 backdrop-blur-sm text-white p-3 rounded-full z-20 hover:bg-socabeg-blue/30 transition-all duration-300 border border-socabeg-blue/30 shadow-lg hidden md:block"
          aria-label={t('home.common.next')}
          whileHover={{ x: 3 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaChevronRight size={16} />
        </motion.button>

        {/* Indicateurs de diapositives modernisés */}
        <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-20">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 transition-all duration-300 rounded-full ${
                index === currentSlide ? 'bg-socabeg-red w-12' : 'bg-socabeg-blue/40 w-6 hover:bg-socabeg-blue/60'
              }`}
              aria-label={t('home.common.goToSlide', { index: index + 1 })}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            ></motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
